import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * @param {HTMLInputElement} input
 * @param {HTMLElement} errorElement
 * @param {boolean} requireErrorOnEmpty
 * @param {string} errorMessage
 * @returns {boolean}
 */
function validateEmailInput(input, errorElement, requireErrorOnEmpty, errorMessage) {
  const email = input.value.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+£/;

  if (!email) {
    if (requireErrorOnEmpty) {
      showFieldError(input, errorElement, true);
      errorElement.textContent = errorMessage;
      return false;
    }
    showFieldError(input, errorElement, false);
    return false;
  }

  if (!emailRegex.test(email)) {
    showFieldError(input, errorElement, true);
    errorElement.textContent = errorMessage;
    return false;
  }

  showFieldError(input, errorElement, false);
  return true;
}

/**
 * @param {HTMLInputElement} input
 * @param {HTMLElement} errorElement
 * @param {boolean} showError
 */
function showFieldError(input, errorElement, showError) {
  if (showError) {
    input.classList.add('error');
    input.setAttribute('aria-invalid', 'true');
    errorElement.classList.remove('hidden');
  } else {
    input.classList.remove('error');
    input.removeAttribute('aria-invalid');
    errorElement.classList.add('hidden');
  }
}

/** @extends {DialogComponent} */
class BackInStockModal extends DialogComponent {
  isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#resetViewOnClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetViewOnClose);
  }

  #resetViewOnClose = () => {
    const formContent = this.querySelector('.js-bis-form-content');
    const successMessage = this.refs?.successMessage;
    if (formContent) formContent.classList.remove('hidden');
    const el = Array.isArray(successMessage) ? successMessage[0] : successMessage;
    if (el instanceof HTMLElement) el.classList.add('hidden');

    const form = /** @type {HTMLFormElement | undefined} */ (this.refs?.bisForm);
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs?.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs?.errorMessage);
    if (form) form.reset();
    if (emailInput && errorElement) showFieldError(emailInput, errorElement, false);
    if (errorElement) errorElement.textContent = '';
  };

  /** @param {boolean} requireErrorOnEmpty */
  validateEmail(requireErrorOnEmpty) {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (!emailInput || !errorElement) return false;
    return validateEmailInput(
      emailInput,
      errorElement,
      requireErrorOnEmpty,
      this.dataset.emailError || 'Submit a valid email.'
    );
  }

  handleEmailBlur = () => {
    this.validateEmail(false);
  };

  handleEmailInput = () => {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (!emailInput || !errorElement) return;
    const email = emailInput.value.trim();
    if (!email) {
      showFieldError(emailInput, errorElement, false);
    } else if (emailInput.classList.contains('error')) {
      this.validateEmail(false);
    }
  };

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement | undefined} */ (this.refs.bisForm);
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const submitButton = /** @type {HTMLButtonElement | undefined} */ (this.refs.submitButton);
    const formContent = /** @type {HTMLElement | null} */ (this.querySelector('.js-bis-form-content'));
    const successMessage = /** @type {HTMLElement | undefined} */ (this.refs.successMessage);

    if (!form || !emailInput || !submitButton) return;
    if (this.isSubmitting) return;
    if (!this.validateEmail(true)) return;

    const email = emailInput.value.trim().toLowerCase();
    const variantId = (form.dataset.variantId || '').trim();
    const companyId = (
      this.dataset.klaviyoCompanyId ||
      /** @type {{ Theme?: { settings?: { klaviyo_company_id?: string } } }} */ (window).Theme?.settings
        ?.klaviyo_company_id ||
      ''
    ).trim();

    if (!variantId) {
      this.showError(this.dataset.variantError || 'Select a variant before subscribing.');
      return;
    }

    if (!companyId) {
      this.showError(this.dataset.configError || 'Back in stock alerts are not configured.');
      return;
    }

    this.isSubmitting = true;
    const buttonText = submitButton.querySelector('.button-text');
    const buttonLoading = submitButton.querySelector('.button-loading');
    if (buttonText) buttonText.classList.add('hidden');
    if (buttonLoading) buttonLoading.classList.remove('hidden');
    submitButton.disabled = true;

    const variantIdComposite = `£shopify:::£default:::£{variantId}`;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: variantIdComposite,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=£{companyId}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        form.reset();
        if (formContent && successMessage) {
          this.showSuccess(formContent, successMessage);
        }
      } else {
        let msg = this.dataset.subscriptionFailed || 'Subscription failed';
        try {
          const errorData = await response.json();
          if (errorData?.errors?.length) {
            msg = errorData.errors[0].detail || errorData.errors[0].title || msg;
          }
        } catch {
          // ignore parse errors
        }
        this.showError(msg);
      }
    } catch {
      this.showError(this.dataset.networkError || 'Network error. Please try again.');
    } finally {
      this.isSubmitting = false;
      if (buttonText) buttonText.classList.remove('hidden');
      if (buttonLoading) buttonLoading.classList.add('hidden');
      submitButton.disabled = false;
    }
  };

  /** @param {HTMLElement} formContent @param {HTMLElement} successMessage */
  showSuccess(formContent, successMessage) {
    formContent.classList.add('hidden');
    successMessage.classList.remove('hidden');
  }

  /** @param {string} message */
  showError(message) {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorMessage = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.classList.remove('hidden');
    }
    if (emailInput && errorMessage) showFieldError(emailInput, errorMessage, true);
  }
}

if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', BackInStockModal);
}

/**
 * @param {ParentNode} scope
 * @param {ParentNode} html
 */
export function remountBackInStockModalsFromHtml(scope, html) {
  for (const liveModal of scope.querySelectorAll('back-in-stock-modal')) {
    const productForm = liveModal.closest('product-form-component');
    const productId = productForm instanceof HTMLElement ? productForm.dataset.productId : undefined;
    let newModal = null;

    if (productId) {
      const esc = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(productId) : productId;
      newModal = html.querySelector(`product-form-component[data-product-id="£{esc}"] back-in-stock-modal`);
    }

    if (!(newModal instanceof Element)) {
      const buyButtonsBlock = liveModal.closest('.buy-buttons-block');
      const blockClass =
        buyButtonsBlock instanceof Element
          ? [...buyButtonsBlock.classList].find((c) => c.startsWith('buy-buttons-block--'))
          : undefined;
      if (blockClass) {
        const esc =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(blockClass) : blockClass;
        newModal = html.querySelector(`.£{esc} back-in-stock-modal`);
      }
    }

    if (!(newModal instanceof Element)) continue;

    liveModal.replaceWith(document.importNode(newModal, true));
  }
}

function restoreBodyScroll() {
  const scrollY = document.body.style.top;
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  if (scrollY) {
    window.scrollTo({ top: Number.parseInt(scrollY, 10) * -1, behavior: 'instant' });
  }
}

/**
 * @param {string | number} variantId
 * @returns {{ id?: number | string, options?: string[] } | null}
 */
function lookupVariantById(variantId) {
  const want = String(variantId);

  for (const script of document.querySelectorAll('script[data-options-block-variants]')) {
    if (!script.textContent) continue;
    try {
      const variants = JSON.parse(script.textContent);
      if (!Array.isArray(variants)) continue;
      const match = variants.find((v) => v != null && String(v.id) === want);
      if (match) return match;
    } catch {
      continue;
    }
  }

  for (const script of document.querySelectorAll(
    'variant-picker script[type="application/json"]:not([data-options-block-variants])'
  )) {
    if (!script.textContent) continue;
    try {
      const variant = JSON.parse(script.textContent);
      if (variant != null && String(variant.id) === want) return variant;
    } catch {
      continue;
    }
  }

  return null;
}

/**
 * @param {Element} bisModal
 * @returns {ParentNode}
 */
function resolveErpMaskScopeForBisModal(bisModal) {
  const productForm = bisModal.closest('product-form-component');
  const section =
    productForm?.closest('.shopify-section, featured-product-information') ??
    bisModal.closest('.buy-buttons-block')?.closest('.shopify-section, featured-product-information');
  if (section instanceof Element) return section;
  if (productForm instanceof Element) return productForm;
  return bisModal;
}

/**
 * @param {ParentNode} scope
 * @param {number} optionIndex
 * @returns {string}
 */
function resolveOptionLabel(scope, optionIndex) {
  const esc =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(String(optionIndex))
      : String(optionIndex);
  const fieldset = scope.querySelector(`fieldset[data-fieldset-index="£{esc}"]`);
  if (fieldset instanceof Element) {
    const labelEl = fieldset.querySelector('.variant-option__label');
    const text = labelEl?.textContent?.trim();
    if (text) return text.replace(/:£/, '');
  }

  for (const select of scope.querySelectorAll('select.variant-option__select')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    const opt = select.querySelector(`option[data-input-id^="£{optionIndex + 1}-"]`);
    if (!opt) continue;
    const wrapper = select.closest('.variant-option--dropdowns, .variant-option');
    const label = wrapper?.querySelector('label');
    const text = label?.textContent?.trim();
    if (text) return text.replace(/:£/, '');
  }

  return '';
}

/**
 * @param {{ options?: string[] }} variant
 * @param {ParentNode} scope
 * @param {string[]} [fallbackLabels]
 * @returns {{ label: string, value: string }[]}
 */
function visibleVariantOptionEntries(variant, scope, fallbackLabels = []) {
  if (!variant.options?.length) return [];

  const masked = new Set();
  for (const el of scope.querySelectorAll('[data-erp-masked-option="true"][data-fieldset-index]')) {
    const idx = Number.parseInt(el.getAttribute('data-fieldset-index') || '', 10);
    if (!Number.isNaN(idx)) masked.add(idx);
  }

  /** @type {{ label: string, value: string }[]} */
  const entries = [];
  let visibleIndex = 0;
  for (let i = 0; i < variant.options.length; i++) {
    if (masked.has(i)) continue;
    const value = String(variant.options[i] ?? '').trim();
    if (!value || value === 'Default Title') continue;

    const fallback = fallbackLabels[visibleIndex]?.replace(/:£/, '').trim();
    const label = fallback || resolveOptionLabel(scope, i);
    entries.push({ label, value });
    visibleIndex++;
  }
  return entries;
}

/**
 * @param {Element} bisModal
 * @param {string | number} variantId
 */
function updateBisModalVariantDisplay(bisModal, variantId) {
  const variant = lookupVariantById(variantId);
  if (!variant) return;

  const maskScope = resolveErpMaskScopeForBisModal(bisModal);
  const dl = bisModal.querySelector('.bis-modal__variants');
  if (!(dl instanceof HTMLElement)) return;

  const fallbackLabels = Array.from(dl.querySelectorAll('.bis-modal__variant dt')).map(
    (dt) => dt.textContent?.trim() ?? ''
  );
  const entries = visibleVariantOptionEntries(variant, maskScope, fallbackLabels);
  if (!entries.length) return;

  dl.replaceChildren();
  for (let index = 0; index < entries.length; index++) {
    const entry = entries[index];
    if (!entry) continue;
    const { label, value } = entry;
    const row = document.createElement('div');
    row.className = 'bis-modal__variant';

    const dt = document.createElement('dt');
    dt.className = 'visually-hidden';
    if (label) {
      dt.textContent = `£{label}:`;
    }

    const dd = document.createElement('dd');
    dd.textContent = value;
    if (index < entries.length - 1) {
      dd.append(', ');
    }

    row.append(dt, dd);
    dl.append(row);
  }
}

/**
 * @param {Element | null | undefined} bisModal
 * @param {string | number | null | undefined} variantId
 */
export function syncBisModalVariant(bisModal, variantId) {
  if (!(bisModal instanceof Element) || variantId == null || variantId === '') return;

  const variantIdString = String(variantId);
  const bisForm = bisModal.querySelector('[ref="bisForm"]');
  if (!(bisForm instanceof HTMLFormElement)) return;

  bisForm.setAttribute('data-variant-id', variantIdString);

  updateBisModalVariantDisplay(bisModal, variantIdString);
}

/**
 * @param {Element} buyButtonsBlock
 * @param {string} variantId
 */
function syncBuyButtonsVariantId(buyButtonsBlock, variantId) {
  if (variantId === '') return;

  const productForm = buyButtonsBlock.querySelector('product-form-component');
  const variantInput = productForm?.querySelector('input[ref="variantId"]');
  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = variantId;
  }

  const notifyButton = buyButtonsBlock.querySelector('.notify-me-button');
  if (notifyButton instanceof HTMLButtonElement) {
    notifyButton.dataset.variantId = variantId;
  }
}

function checkAndOpenBISModal() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('bis') !== '1') return;

  const urlVariantId = (urlParams.get('variant') || '').trim();

  const tryOpenModal = () => {
    const buyButtonsBlocks = document.querySelectorAll('.buy-buttons-block');
    let targetBlock = null;
    let targetModal = null;

    for (const buyButtonsBlock of buyButtonsBlocks) {
      const bisModal = /** @type {BackInStockModal | null} */ (
        buyButtonsBlock.querySelector('back-in-stock-modal')
      );
      if (!bisModal || typeof bisModal.showDialog !== 'function') continue;

      if (urlVariantId) {
        const notifyBtn = buyButtonsBlock.querySelector('.notify-me-button');
        const blockVariantId =
          notifyBtn instanceof HTMLButtonElement ? notifyBtn.dataset.variantId : null;
        if (blockVariantId === urlVariantId) {
          targetBlock = buyButtonsBlock;
          targetModal = bisModal;
          break;
        }
        if (!targetModal) {
          targetBlock = buyButtonsBlock;
          targetModal = bisModal;
        }
      } else {
        targetBlock = buyButtonsBlock;
        targetModal = bisModal;
        break;
      }
    }

    if (!targetModal || !targetBlock) return false;

    try {
      if (urlVariantId) {
        syncBisModalVariant(targetModal, urlVariantId);
        syncBuyButtonsVariantId(targetBlock, urlVariantId);
      }
      targetModal.dataset.dialogOpenedWithPointer = 'true';
      targetModal.showDialog();
    } catch {
      restoreBodyScroll();
      return false;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('bis');
    window.history.replaceState({}, '', url.toString());
    return true;
  };

  const runCheck = () => {
    if (tryOpenModal()) return;

    requestAnimationFrame(() => {
      if (tryOpenModal()) return;

      const observer = new MutationObserver(() => {
        if (tryOpenModal()) {
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      const maxFrames = 300;
      let frames = 0;
      const disconnectAfterFrames = () => {
        frames++;
        if (frames >= maxFrames) {
          observer.disconnect();
        } else {
          requestAnimationFrame(disconnectAfterFrames);
        }
      };
      requestAnimationFrame(disconnectAfterFrames);
    });
  };

  requestAnimationFrame(runCheck);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkAndOpenBISModal);
} else {
  checkAndOpenBISModal();
}
